import { useState, useEffect } from 'react';
import { apiClient } from '../api/client';

interface SubscriptionStatus {
  plan_type: string;
  status: string;
  is_trial: boolean;
  trial_end?: string;
  documents_used: number;
  documents_limit: number;
  ai_queries_used: number;
  ai_queries_limit: number;
  templates_used?: number;
  templates_limit?: number;
  claude_model: string;
  features: string[];
  current_period_end?: string;
  can_upgrade: boolean;
}

interface SubscriptionLimits {
  isDocumentLimitReached: boolean;
  isAIQueryLimitReached: boolean;
  documentUsagePercentage: number;
  aiQueryUsagePercentage: number;
  shouldShowDocumentWarning: boolean;
  shouldShowAIQueryWarning: boolean;
}

export const useSubscription = () => {
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/subscription/status');
      setSubscription(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setError('Failed to load subscription status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscription();
  }, []);

  const getLimits = (): SubscriptionLimits => {
    if (!subscription) {
      return {
        isDocumentLimitReached: false,
        isAIQueryLimitReached: false,
        documentUsagePercentage: 0,
        aiQueryUsagePercentage: 0,
        shouldShowDocumentWarning: false,
        shouldShowAIQueryWarning: false,
      };
    }

    const documentUsagePercentage = (subscription.documents_used / subscription.documents_limit) * 100;
    const aiQueryUsagePercentage = (subscription.ai_queries_used / subscription.ai_queries_limit) * 100;

    return {
      isDocumentLimitReached: subscription.documents_used >= subscription.documents_limit,
      isAIQueryLimitReached: subscription.ai_queries_used >= subscription.ai_queries_limit,
      documentUsagePercentage,
      aiQueryUsagePercentage,
      shouldShowDocumentWarning: documentUsagePercentage >= 80,
      shouldShowAIQueryWarning: aiQueryUsagePercentage >= 80,
    };
  };

  // Whether we actually know what plan the user is on. Without this, a failed
  // /status request is indistinguishable from "not eligible", so a paying
  // customer got shown the upgrade paywall and lost their KSeF buttons because
  // one request errored.
  const planKnown = subscription !== null;

  // KSeF e-invoicing is gated to the Advanced plan only.
  const isKsefEligible = subscription?.plan_type === 'advanced';

  // Expenses page is available on the Trial (Discovery) and Advanced plans.
  const isExpensesEligible =
    subscription?.is_trial === true || subscription?.plan_type === 'advanced';

  return {
    subscription,
    loading,
    error,
    planKnown,
    refetch: fetchSubscription,
    limits: getLimits(),
    isKsefEligible,
    isExpensesEligible,
  };
};